/**
 * Canvas layout for tweet/quote cards painted onto museum walls.
 *
 * Pure 2D drawing, no three.js: a card is stretched onto whatever quad it lands
 * on, so the canvas has to carry that quad's aspect ratio or the text shears —
 * a 1024x768 canvas on the gallery's 1:3 portrait frames squeezed every glyph
 * to a quarter width.
 *
 * Text starts from a size in *model units* rather than canvas pixels, so every
 * card opens at the same physical reading size and plane/tweet matching by
 * capacity stays meaningful. That base is a starting point, not a ceiling: the
 * type then grows or shrinks to fill the plane it landed on, because matching
 * can only get so close — once the long quotes are placed, a short one still
 * has to hold a big frame, and at base size it left two thirds of the wall bare.
 *
 * ---- What this layer owes to the e-ink screen ------------------------------
 *
 * The layout is modelled on an existing TRMNL e-ink plugin that renders these
 * same records, which had solved several problems this file had not:
 *
 * - Quote and tweet are genuinely different compositions, not one card with a
 *   different tint. A quote is a display pull-quote — rail, big centred text,
 *   byline, no furniture. A tweet is a card with real furniture: name/handle
 *   header, body at reading size, date, divider, engagement row.
 * - Media sits *beside* the text on a wide card, not underneath it. A strip
 *   under the text is the worst possible box for a portrait photo, and most
 *   saved stills are portrait.
 * - The type ceiling is a hierarchy decision, not a maximum. The e-ink footer
 *   stays 16px however large the quote grows, so a two-word quote reads as
 *   3-4x its byline rather than 7x. Ours used to scale the whole ladder on one
 *   number, which meant a two-word tweet also got a two-word-sized byline.
 *   `ladderFor` now damps the chrome — see the note there.
 * - Legibility at the small end is bought with weight and leading, not size.
 * - `overflow-wrap: anywhere`. A token longer than the line has to break, or a
 *   bare URL silently paints straight out of its box.
 *
 * The whole type ladder still moves on one scale (see `ladderFor`). Fitting the
 * heading and the body independently is what lets a grown body outrun its own
 * heading, so the ratios between them are held while the block is fitted.
 */

// Extension is explicit: this module is also loaded straight into a plain ESM
// page for layout previews (`card-preview.html` at the repo root), and a
// browser will not resolve './sources'.
import { isRoamSource } from './sources.js';

const TEXTURE_BUDGET = 1100000; // pixels per canvas
const MIN_CANVAS_SIDE = 384;
const MAX_CANVAS_SIDE = 2048;
const MAX_CANVAS_ASPECT = 6;

// Fractions of the museum's median plane height.
const BODY_TEXT = 0.068;
const HEADER_TEXT = 0.088;

// The nominal line spacing. Real leading eases around this with the fitted
// scale (see `leadingFor`); this is the value the capacity model assumes, and
// changing it moves every frame the palace generates.
const LINE_SPACING = 1.28;

// The type ladder, as ratios off the body size.
const HEADER_RATIO = HEADER_TEXT / BODY_TEXT; // heading : body
const FOOTER_RATIO = 0.7; // attribution : body

// How hard the chrome tracks the body when the body is fitted up or down.
//
// 0 would pin the header and footer to the plane's physical reading size (what
// the e-ink template does, where the footer is a flat 16px however large the
// quote grows). 1 is what this file used to do — one scale for the whole
// ladder, so a two-word quote at 2.5x dragged a 2.5x byline up with it.
//
// Neither end works here, because unlike the e-ink screen our cards are not all
// the same size: on a frame well under the museum median the body is fitted
// *down*, and a header pinned to the plane would still have to outrank it.
// A fractional exponent gives both — at 2.5x the byline grows 1.4x, at 0.55x it
// only falls to 0.79x and stays comfortably above the body.
const CHROME_DAMP = 0.4;

const MIN_TEXT_SCALE = 0.55; // how far the fit may shrink before ellipsising
const MAX_TWEET_SCALE = 2.5; // body text in a card, so a modest ceiling
const MAX_QUOTE_SCALE = 3.2; // a pull-quote is the hero of its frame
const FIT_STEP = 0.94; // geometric, so a step costs the same at either end

// Leading eases against the fitted scale. Grown display text wants to close up
// — at 2.5x, 1.28 leading opens gaps you read as paragraph breaks — and shrunk
// text wants air, because on a wall the small end is read at distance where
// tight lines merge into a grey block.
const LEAD_TIGHT = 1.14;
const LEAD_LOOSE = 1.40;

// Below these scales the body steps up in weight. The e-ink engine eases weight
// continuously from 400 toward 700 as it shrinks; a canvas cannot, because the
// stack below resolves to whatever faces the OS actually has and the browser
// snaps an unavailable weight to the nearest real one. So this is a two-step
// approximation of the same idea, and on a system with only Arial's 400/700 the
// first step is a no-op rather than a surprise.
const WEIGHT_MID = 0.85;
const WEIGHT_HEAVY = 0.68;

// Text is measured at every candidate scale, so an unbounded body would wrap a
// 20,000-character Roam page fifteen times before ellipsising it to four lines.
// Bounding the input is the only lever on the cost, and the same one the e-ink
// template pulls with `truncate: 1500`.
const TEXT_CAP = 2000;

// Above this canvas aspect a still hangs *beside* the text rather than under
// it. 1.15 is not arbitrary: it is the bottom of the widened aspect band that
// `palaceLayout` now hands to saves that carry media, so a photo save built by
// the palace always lands in the split layout, while a photo that happens to
// hang on a portrait frame in the scanned gallery still stacks.
const SPLIT_ASPECT = 1.15;

// Share of the card's inner width the media column takes when split, and of the
// body box it claims before the text is fitted when stacked. The stacked text
// is then drawn top-aligned and the image takes everything actually left over,
// so a one-line caption over a photo still gives the photo most of the card.
const MEDIA_COLUMN = 0.42;
const MEDIA_SHARE = 0.55;

// Furniture is progressive. A 0.55 m frame cannot carry a header, a date row, a
// divider and an engagement row and still have room to say anything, so rows
// are dropped from the bottom up as the card gets tight. These are the minimum
// body lines that must survive for a row to be worth its height.
const LINES_FOR_DATE = 2.5;
const LINES_FOR_STATS = 4;

// Above this many lines a quote stops being centred — see the note in
// `drawRoamQuote`.
const CENTRE_MAX_LINES = 4;

// Heights of the optional rows, in footer-type units.
const DATE_ROW = 1.7;
const STATS_ROW = 2.3;
const HEADER_RESERVE = 1.85; // header row plus its rule
const FOOTER_RESERVE = DATE_ROW + STATS_ROW; // what the capacity model assumes

const PAPER = {
  top: '#fbf9f4',
  bottom: '#f2eee4',
  ink: '#14120f',
  muted: '#6d6659',
  rule: '#d3cabb',
  accent: '#1d9bf0',
  mat: '#c9c2b4',
};

// Roam quotes keep a cool cast against the tweets' warm one. The layouts are
// already unmistakable up close; the tint is what tells them apart across a
// room, where the composition has collapsed to a rectangle of tone.
const VELLUM = {
  top: '#faf9ff',
  bottom: '#efebfb',
  ink: '#17152a',
  muted: '#6b6584',
  rule: '#cdc4ef',
  accent: '#7c3aed',
  mat: '#c6bfdd',
};

const SANS = '"Helvetica Neue", Helvetica, Arial, sans-serif';
// Serif for the quote body only. A pull-quote set in the same grotesque as the
// tweet cards reads as another tweet; the change of voice is doing as much work
// here as the rail and the centring.
const SERIF = 'Georgia, "Times New Roman", serif';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Text metrics for a museum, derived once from all its planes so the same code
// works for the 100x-scaled gallery scan and the unit-scale procedural museum.
export const textMetricsFor = (planes) => {
  const heights = planes.map((p) => p.height).filter((h) => h > 0).sort((a, b) => a - b);
  const median = heights[Math.floor(heights.length / 2)] || 1;
  return { body: BODY_TEXT * median, header: HEADER_TEXT * median };
};

export const canvasSizeFor = (aspect, pixelBudget = TEXTURE_BUDGET) => {
  const a = Math.min(MAX_CANVAS_ASPECT, Math.max(1 / MAX_CANVAS_ASPECT, aspect || 1));
  let height = Math.sqrt(pixelBudget / a);
  let width = height * a;
  const shrink = Math.min(1, MAX_CANVAS_SIDE / Math.max(width, height));
  return {
    width: Math.max(MIN_CANVAS_SIDE, Math.round(width * shrink)),
    height: Math.max(MIN_CANVAS_SIDE, Math.round(height * shrink)),
  };
};

/**
 * The capacity model, exported because `palaceLayout` *inverts* it to size the
 * frames it generates. It used to do that against a copy-pasted set of these
 * numbers with a comment reading "museumCanvas's ratio", which meant every
 * change to the card's chrome silently desynced frame sizing from what the card
 * actually needed — adding the date and engagement rows below would have
 * overrun every frame in the building by about a line and a half.
 */
export const CARD_MODEL = {
  BODY_TEXT,
  HEADER_TEXT,
  LINE_SPACING,
  FOOTER_RATIO,
  HEADER_RESERVE,
  FOOTER_RESERVE,
  MARGIN: 0.9, // share of the plane inside the padding
  CHAR_EM: 0.5, // Arial averages about half the em
  SPLIT_ASPECT,
  MEDIA_COLUMN,
};

// Vertical room the furniture takes at a given base text size, in model units.
// Both `planeCapacity` and the palace's frame solver read this, so the two can
// never again disagree about how much of a frame the card gives back to chrome.
export const chromeReserve = (text) =>
  HEADER_RESERVE * text.header + FOOTER_RESERVE * (text.body * FOOTER_RATIO);

// Roughly how many characters of body text a plane can hold at the museum's
// base text size. This is what a plane is "worth" for matching: a tall skinny
// frame has real area but very few characters per line. Cards fit their type up
// or down from this base, so treat it as a ranking, not a promise — it only has
// to order planes the same way it orders quotes by length.
export const planeCapacity = (plane, text) => {
  const charWidth = text.body * CARD_MODEL.CHAR_EM;
  const lineHeight = text.body * LINE_SPACING;
  const usableWidth = plane.width * CARD_MODEL.MARGIN;
  const usableHeight = plane.height * CARD_MODEL.MARGIN - chromeReserve(text);
  if (usableWidth <= 0 || usableHeight <= 0) return 0;
  return Math.floor(usableWidth / charWidth) * Math.floor(usableHeight / lineHeight);
};

export const contentText = (content) =>
  content.text || content.full_text || content.content || '';

// ---------------------------------------------------------------------------
// Type ladder
// ---------------------------------------------------------------------------

const leadingFor = (scale) => {
  // log2 so the ease is symmetric about 1x: half size and double size travel
  // the same distance, which a linear ramp on `scale` does not.
  const t = clamp((Math.log2(scale) + 1) / 2, 0, 1);
  return LEAD_LOOSE + (LEAD_TIGHT - LEAD_LOOSE) * t;
};

const weightFor = (scale) => {
  if (scale <= WEIGHT_HEAVY) return 600;
  if (scale <= WEIGHT_MID) return 500;
  return 400;
};

// The card's type sizes at a given scale, in canvas pixels. Heading and
// attribution are derived from the body, never clamped separately: a cap on the
// heading alone is what used to flatten it *below* body size on the frames well
// under the museum median, so their titles stopped reading as titles.
const ladderFor = (basePx) => (scale) => {
  const chrome = basePx * Math.pow(scale, CHROME_DAMP);
  return {
    scale,
    body: basePx * scale,
    header: chrome * HEADER_RATIO,
    footer: chrome * FOOTER_RATIO,
    leading: leadingFor(scale),
    weight: weightFor(scale),
  };
};

const font = (family) => (px, weight = 400) => `${weight} ${Math.max(1, px)}px ${family}`;
const sansFont = font(SANS);
const serifFont = font(SERIF);

// ---------------------------------------------------------------------------
// Text fitting
// ---------------------------------------------------------------------------

// Break a token that cannot fit on a line of its own — a URL, a hash, an
// unspaced CJK run. Without this the wrapper emits the token whole and the
// canvas paints it straight through the card's margin and off the edge, which
// is the failure the e-ink template's `overflow-wrap: anywhere` prevents.
const breakToken = (context, token, maxWidth) => {
  const parts = [];
  let part = '';
  for (const ch of token) {
    if (part && context.measureText(part + ch).width > maxWidth) {
      parts.push(part);
      part = ch;
    } else {
      part += ch;
    }
  }
  if (part) parts.push(part);
  return parts;
};

// Wrap `text` into lines no wider than `maxWidth`, stopping at `maxLines`.
//
// Hard line breaks in the source are honoured, because a tweet's paragraphing
// is usually the only structure it has. Runs of blank lines collapse to a
// single break rather than becoming blank lines: on a 0.6 m frame an empty line
// costs as much as a sentence.
const wrapText = (context, text, maxWidth, maxLines) => {
  const lines = [];
  const paragraphs = String(text).split(/\n+/);

  for (const paragraph of paragraphs) {
    const words = paragraph.split(/[ \t]+/).filter(Boolean);
    if (!words.length) continue;
    let line = '';

    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (context.measureText(candidate).width <= maxWidth) {
        line = candidate;
        continue;
      }
      if (line) {
        lines.push(line);
        if (lines.length === maxLines) return { lines, overflow: true };
        line = '';
      }
      // The word alone still does not fit: split it rather than overflow.
      if (context.measureText(word).width > maxWidth) {
        const parts = breakToken(context, word, maxWidth);
        for (let i = 0; i < parts.length - 1; i++) {
          lines.push(parts[i]);
          if (lines.length === maxLines) return { lines, overflow: true };
        }
        line = parts[parts.length - 1] || '';
      } else {
        line = word;
      }
    }

    if (line) {
      lines.push(line);
      if (lines.length === maxLines) {
        // Only an overflow if there is more text behind it.
        const more = paragraph !== paragraphs[paragraphs.length - 1];
        if (more) return { lines, overflow: true };
      }
    }
  }
  return { lines, overflow: false };
};

const ellipsise = (context, line, maxWidth) => {
  let out = line;
  while (out.length > 1 && context.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
};

// Largest scale at which the body still fits, searched from the top down so a
// short quote grows into a big frame instead of floating in it. `boxFor` takes
// the whole ladder because the body's room depends on how much the heading
// above and the furniture below reserve, and both move with it. At the floor,
// ellipsise rather than shrink further.
const fitCard = (context, text, ladder, boxFor, faceFor, maxScale) => {
  const measure = (scale) => {
    const type = ladder(scale);
    const box = boxFor(type);
    context.font = faceFor(type.body, type.weight);
    const maxLines = Math.max(1, Math.floor(box.height / (type.body * type.leading)));
    return { type, box, ...wrapText(context, text, box.width, maxLines) };
  };

  for (let scale = maxScale; scale > MIN_TEXT_SCALE; scale *= FIT_STEP) {
    const fitted = measure(scale);
    if (!fitted.overflow) return fitted;
  }

  const fitted = measure(MIN_TEXT_SCALE);
  if (fitted.lines.length) {
    const last = fitted.lines.length - 1;
    fitted.lines[last] = ellipsise(context, fitted.lines[last], fitted.box.width);
  }
  return fitted;
};

// Draw a wrapped block, optionally centred in whatever vertical room is left.
// The fit normally leaves under a line of slack, but a quote short enough to
// still be swimming at the ceiling needs its remainder split rather than dumped
// at the bottom. Returns the height actually painted, which is what decides
// where a stacked still starts.
const drawLines = (context, lines, x, top, type, boxHeight = 0, align = 'left', width = 0) => {
  const step = type.body * type.leading;
  const block = lines.length * step;
  const offset = Math.max(0, (boxHeight - block) / 2);
  const originX = align === 'center' ? x + width / 2 : x;
  context.textAlign = align === 'center' ? 'center' : 'left';
  context.textBaseline = 'alphabetic';
  lines.forEach((line, i) =>
    context.fillText(line, originX, top + offset + type.body + i * step));
  context.textAlign = 'left';
  return block;
};

// How far a heading may shrink to fit its width before it is truncated
// instead. A name at 70% of heading size is still a heading; below that it
// stops being one. Expressed against the heading's own size rather than the
// body's, because past scale ~1.5 the damped chrome puts the heading *below*
// the body and a body-relative floor lands above the ceiling it is bounding.
const HEAD_FLOOR = 0.7;

// Fit a single unwrappable line (a handle, a page title) to `maxWidth`. Shrink
// first — a truncated @name is worse than a slightly smaller one — but never
// below `minPx`, or the heading stops outranking what it introduces. Past
// that, ellipsise.
const fitLine = (context, line, faceFor, maxWidth, basePx, minPx, weight = 700) => {
  for (let px = basePx; px > minPx; px *= FIT_STEP) {
    context.font = faceFor(px, weight);
    if (context.measureText(line).width <= maxWidth) return { line, px };
  }
  context.font = faceFor(minPx, weight);
  if (context.measureText(line).width <= maxWidth) return { line, px: minPx };
  return { line: ellipsise(context, line, maxWidth), px: minPx };
};

// A row of small metadata segments separated by bullets, painted left to right
// and truncated by whole segments. Dropping "07:19 AM" is a better failure than
// ellipsising it, so the row sheds from the right until what is left fits.
// Callers order the row by what they want to keep: a tweet leads with its date,
// a quote with its author, and either way the tail goes first.
const drawSegments = (context, segments, x, baseline, maxWidth, type, palette) => {
  const gap = type.footer * 0.42;
  context.font = sansFont(type.footer, 400);
  context.textBaseline = 'alphabetic';

  const kept = [];
  let used = 0;
  for (const segment of segments.filter(Boolean)) {
    const w = context.measureText(segment).width;
    const bulletW = kept.length ? context.measureText('•').width + gap * 2 : 0;
    if (used + bulletW + w > maxWidth) break;
    kept.push(segment);
    used += bulletW + w;
  }

  let cursor = x;
  kept.forEach((segment, i) => {
    if (i) {
      context.fillStyle = palette.rule;
      context.fillText('•', cursor + gap, baseline);
      cursor += gap * 2 + context.measureText('•').width;
    }
    context.fillStyle = palette.muted;
    context.fillText(segment, cursor, baseline);
    cursor += context.measureText(segment).width;
  });
};

// ---------------------------------------------------------------------------
// Furniture
// ---------------------------------------------------------------------------

const paper = (context, width, height, palette) => {
  const gradient = context.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, palette.top);
  gradient.addColorStop(1, palette.bottom);
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
};

// A hairline inset keyline. Reads as the bevel of a mount rather than a border,
// which is the point: the frame moulding is real geometry in the palace, so the
// card should not draw a second competing frame inside it.
const keyline = (context, width, height, pad, palette) => {
  const inset = pad * 0.34;
  context.strokeStyle = palette.rule;
  context.lineWidth = Math.max(1, pad * 0.045);
  context.strokeRect(inset, inset, width - inset * 2, height - inset * 2);
};

// The engagement glyphs, lifted from the e-ink template (24x24 viewBox, so they
// scale by size/24). Built lazily: `palaceLayout` imports `contentText` from
// this module and runs before any canvas exists, and Path2D is not defined
// outside a browser.
const ICON_PATHS = {
  reply: 'M14.046 2.242l-4.148-.01h-.002c-4.374 0-7.8 3.427-7.8 7.802 0 4.098 3.186 7.206 7.465 7.37v3.828c0 .108.044.286.12.403.142.225.384.347.632.347.138 0 .277-.038.402-.118.264-.168 6.473-4.14 8.088-5.506 1.902-1.61 3.04-3.97 3.043-6.312v-.017c-.006-4.367-3.43-7.787-7.8-7.788zm3.787 12.972c-1.134.96-4.862 3.405-6.772 4.643V16.67c0-.414-.335-.75-.75-.75h-.396c-3.66 0-6.318-2.476-6.318-5.886 0-3.534 2.768-6.302 6.3-6.302l4.147.01h.002c3.532 0 6.3 2.766 6.302 6.296-.003 1.91-.942 3.844-2.514 5.176z',
  retweet: 'M23.77 15.67c-.292-.293-.767-.293-1.06 0l-2.22 2.22V7.65c0-2.068-1.683-3.75-3.75-3.75h-5.85c-.414 0-.75.336-.75.75s.336.75.75.75h5.85c1.24 0 2.25 1.01 2.25 2.25v10.24l-2.22-2.22c-.293-.293-.768-.293-1.06 0s-.294.768 0 1.06l3.5 3.5c.145.147.337.22.53.22s.383-.072.53-.22l3.5-3.5c.294-.292.294-.767 0-1.06zm-10.66 3.28H7.26c-1.24 0-2.25-1.01-2.25-2.25V6.46l2.22 2.22c.148.147.34.22.532.22s.384-.073.53-.22c.293-.293.293-.768 0-1.06l-3.5-3.5c-.293-.294-.768-.294-1.06 0l-3.5 3.5c-.294.292-.294.767 0 1.06s.767.293 1.06 0l2.22-2.22V16.7c0 2.068 1.683 3.75 3.75 3.75h5.85c.414 0 .75-.336.75-.75s-.337-.75-.75-.75z',
  like: 'M20.884 13.19c-1.351 2.48-4.001 5.12-8.379 7.67l-.503.3-.504-.3c-4.379-2.55-7.029-5.19-8.382-7.67-1.36-2.5-1.41-4.86-.514-6.67.887-1.79 2.647-2.91 4.601-3.01 1.651-.09 3.368.56 4.798 2.01 1.429-1.45 3.146-2.1 4.796-2.01 1.954.1 3.714 1.22 4.601 3.01.896 1.81.846 4.17-.514 6.67z',
};
let icons = null;
const iconFor = (name) => {
  if (!icons) {
    icons = {};
    for (const [key, d] of Object.entries(ICON_PATHS)) icons[key] = new Path2D(d);
  }
  return icons[name];
};

const compactCount = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(n < 10000000 ? 1 : 0)}M`.replace('.0', '');
  if (n >= 1000) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}K`.replace('.0', '');
  return String(n);
};

// Engagement row: glyph, count, glyph, count. Zero counts are dropped entirely
// rather than drawn as "0" — an unanswered tweet should not carry a row of
// zeroes as if that were information about it.
const drawStats = (context, tweet, x, y, size, palette) => {
  const stats = [
    ['reply', tweet.reply_count],
    ['retweet', tweet.retweet_count],
    ['like', tweet.like_count],
  ].filter(([, n]) => Number(n) > 0);
  if (!stats.length) return;

  const glyph = size;
  const gap = size * 0.34;
  context.font = sansFont(size * 0.95, 500);
  context.textBaseline = 'alphabetic';

  let cursor = x;
  for (const [name, count] of stats) {
    context.save();
    context.translate(cursor, y - glyph * 0.82);
    context.scale(glyph / 24, glyph / 24);
    context.fillStyle = palette.muted;
    context.fill(iconFor(name));
    context.restore();
    cursor += glyph + gap * 0.6;

    const label = compactCount(Number(count));
    context.fillStyle = palette.muted;
    context.fillText(label, cursor, y);
    cursor += context.measureText(label).width + gap * 1.6;
  }
};

// Fit a still into `rect`, centred, letterboxed on a warm mat rather than
// stretched — a squashed photograph is worse than an empty margin. Videos get
// their poster frame plus a play mark, since that is all the archive keeps.
const drawMedia = (context, rect, { image, isVideo }, palette) => {
  if (rect.h < 32 || rect.w < 32) return;
  const scale = Math.min(rect.w / image.naturalWidth, rect.h / image.naturalHeight);
  const w = image.naturalWidth * scale;
  const h = image.naturalHeight * scale;
  const x = rect.x + (rect.w - w) / 2;
  const y = rect.y + (rect.h - h) / 2;

  context.drawImage(image, x, y, w, h);
  context.strokeStyle = palette.mat;
  context.lineWidth = Math.max(1, Math.min(w, h) * 0.006);
  context.strokeRect(x, y, w, h);

  if (!isVideo) return;
  const r = Math.min(w, h) * 0.11;
  const cx = x + w / 2;
  const cy = y + h / 2;
  context.fillStyle = 'rgba(17, 17, 17, 0.62)';
  context.beginPath();
  context.arc(cx, cy, r, 0, 2 * Math.PI);
  context.fill();
  context.fillStyle = '#ffffff';
  context.beginPath();
  context.moveTo(cx - r * 0.32, cy - r * 0.46);
  context.lineTo(cx + r * 0.52, cy);
  context.lineTo(cx - r * 0.32, cy + r * 0.46);
  context.closePath();
  context.fill();
};

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The likes export carries pre-formatted `tweet_date`/`tweet_time` strings and
// the normalized records carry an ISO `created_at`; prefer the former, since it
// is already in the archive's own voice, and format the latter when it is all
// there is.
const dateSegments = (content) => {
  const meta = content._raw_metadata || content.enhanced_metadata || {};
  if (meta.tweet_date) return [meta.tweet_date, meta.tweet_time].filter(Boolean);
  const iso = content.created_at || content.date;
  if (!iso) return [];
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return [];
  return [`${monthNames[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`];
};

// Twitter appends the media's own t.co link to the tweet text. A shortener hash
// is unreadable by construction — nobody copies `t.co/MFFkcJJLWa` off a wall —
// and it is the one token in the archive guaranteed to need mid-word breaking,
// so it costs two lines to say nothing. It goes whether or not the still loads:
// keying this off the loaded image left a bare URL as the entire body of every
// media save whose picture 404'd.
const TRAILING_LINK = /(\s*https?:\/\/t\.co\/\w+)+\s*$/;

const bodyTextFor = (content, media) => {
  const raw = contentText(content).trim();
  const stripped = raw.replace(TRAILING_LINK, '').trim();
  // A save that is nothing but a link keeps it, unless a still is there to be
  // the content instead.
  return (stripped || (media ? '' : raw)).slice(0, TEXT_CAP);
};

/**
 * Tweet card: name and handle, rule, body, date, engagement, and the save's
 * still if it has one.
 *
 * The name/handle split is the e-ink template's: both at the same size,
 * separated on weight rather than scale. A handle set smaller than the name
 * turns into a one-pixel stroke on a distant frame, which is the state this
 * card was in when it drew nothing but a bold `@handle`.
 */
const drawTweet = ({ canvas, context, ladder, pad }, tweet, media = null) => {
  const palette = PAPER;
  paper(context, canvas.width, canvas.height, palette);
  keyline(context, canvas.width, canvas.height, pad, palette);

  const name = tweet.user?.name || tweet.author || 'Unknown';
  const handle = tweet.user?.screen_name ? `@${tweet.user.screen_name}` : '';

  const body = bodyTextFor(tweet, media);
  // A media-only save has no caption to fit; anything else keeps the text
  // first and gives the still what is left.
  const hasText = body.length > 0 || !media;

  const innerW = canvas.width - pad * 2;
  const split = !!media && canvas.width / canvas.height >= SPLIT_ASPECT;
  const gutter = split ? pad * 0.9 : 0;
  const mediaW = split ? (innerW - gutter) * MEDIA_COLUMN : 0;
  const textW = innerW - mediaW - gutter;

  const bodyTop = (type) => pad + type.header * HEADER_RESERVE;

  // What furniture survives at this type size. Computed from the card, not from
  // the fitted box, so the same answer comes back during fitting and during
  // drawing — the e-ink template hand-tuned its pixel budget every time a row
  // changed height, which is exactly the coupling this avoids.
  const furnitureFor = (type) => {
    const free = canvas.height - bodyTop(type) - pad;
    const line = type.body * type.leading;
    const dateH = type.footer * DATE_ROW;
    const statsH = type.footer * STATS_ROW;
    const date = free - dateH >= line * LINES_FOR_DATE;
    const stats = date && free - dateH - statsH >= line * LINES_FOR_STATS;
    return { date, stats, height: (date ? dateH : 0) + (stats ? statsH : 0) };
  };

  const boxFor = (type) => {
    const free = canvas.height - bodyTop(type) - pad - furnitureFor(type).height;
    return {
      width: textW,
      // Stacked media reserves its share up front; split media takes a column,
      // so the text keeps the full height and only loses width.
      height: free * (media && !split ? 1 - MEDIA_SHARE : 1),
    };
  };

  const fitted = hasText
    ? fitCard(context, body || 'No text available', ladder, boxFor, sansFont, MAX_TWEET_SCALE)
    : { lines: [], type: ladder(1), box: boxFor(ladder(1)) };
  const { lines, type, box } = fitted;
  const furniture = furnitureFor(type);

  // Header. Name and handle are fitted as a *pair*: sized independently the
  // name takes the full width at heading size and leaves the handle nothing, so
  // "@divya_venn" simply vanished off a card 1.04 m wide that had room for it.
  // Shrink both together; only if they still will not both fit does the handle
  // go, and then the name gets the width back to itself.
  //
  // The floor is a fraction of the header, not the body size. Past scale ~1.5
  // the damped chrome puts the header *below* the body on purpose, so a floor
  // of `type.body` is above the ceiling on exactly the short, large-type cards
  // with the most room to spare — the loop then runs once at full size, or (as
  // `px > type.body`) not at all, and the handle goes for no reason.
  const gapAt = (px) => px * 0.45;
  const headFloor = type.header * HEAD_FLOOR;
  let head = null;
  if (handle) {
    for (let px = type.header; px >= headFloor; px *= FIT_STEP) {
      context.font = sansFont(px, 700);
      const nameW = context.measureText(name).width;
      context.font = sansFont(px, 400);
      if (nameW + gapAt(px) + context.measureText(handle).width <= innerW) {
        head = { px, name, nameW, handle };
        break;
      }
    }
  }
  if (!head) {
    // Same floor as the pair. Handing this `type.body` truncated names on
    // large-type cards at a size *above* their own heading size.
    const solo = fitLine(context, name, sansFont, innerW, type.header, headFloor, 700);
    context.font = sansFont(solo.px, 700);
    head = { px: solo.px, name: solo.line, nameW: context.measureText(solo.line).width, handle: null };
  }

  context.font = sansFont(head.px, 700);
  context.fillStyle = palette.ink;
  context.textBaseline = 'alphabetic';
  context.fillText(head.name, pad, pad + type.header);
  if (head.handle) {
    context.font = sansFont(head.px, 400);
    context.fillStyle = palette.muted;
    context.fillText(head.handle, pad + head.nameW + gapAt(head.px), pad + type.header);
  }

  const ruleY = pad + type.header * 1.25;
  context.strokeStyle = palette.accent;
  context.lineWidth = Math.max(2, type.header * 0.06);
  context.beginPath();
  context.moveTo(pad, ruleY);
  context.lineTo(canvas.width - pad, ruleY);
  context.stroke();

  // Without a still the block centres in its box as it always has; with one it
  // sits tight under the rule so the image starts as high as the text allows.
  context.fillStyle = palette.ink;
  context.font = sansFont(type.body, type.weight);
  const drawn = drawLines(context, lines, pad, bodyTop(type), type,
    media && !split ? 0 : box.height);

  if (media) {
    const top = split
      ? bodyTop(type)
      : bodyTop(type) + drawn + (lines.length ? type.body * 0.7 : 0);
    const bottom = canvas.height - pad - furniture.height;
    drawMedia(context, {
      x: split ? pad + textW + gutter : pad,
      y: top,
      w: split ? mediaW : innerW,
      h: bottom - top,
    }, media, palette);
  }

  // Footer, drawn from the bottom up so the rows sit on the padding whatever
  // the body above them did.
  let baseline = canvas.height - pad;
  if (furniture.stats) {
    drawStats(context, tweet, pad, baseline, type.footer * 1.05, palette);
    baseline -= type.footer * STATS_ROW;
  }
  if (furniture.date) {
    const segments = dateSegments(tweet);
    if (segments.length) {
      drawSegments(context, segments, pad, baseline - type.footer * 0.35, innerW, type, palette);
    }
    if (furniture.stats) {
      const dividerY = baseline + type.footer * 0.5;
      context.strokeStyle = palette.rule;
      context.lineWidth = Math.max(1, type.footer * 0.045);
      context.beginPath();
      context.moveTo(pad, dividerY);
      context.lineTo(canvas.width - pad, dividerY);
      context.stroke();
    }
  }
};

/**
 * Roam quote: a display pull-quote on a rail, not a card.
 *
 * The rail is the e-ink template's two-column grid rendered directly — the dot
 * centres on the title's line box and the rule runs down under the dot. It
 * costs one column of width and is the whole reason a quote is identifiable as
 * a quote from across a room, before any of the text resolves.
 */
const drawRoamQuote = ({ canvas, context, ladder, pad }, quote) => {
  const palette = VELLUM;
  paper(context, canvas.width, canvas.height, palette);
  keyline(context, canvas.width, canvas.height, pad, palette);

  // Roam page titles are pathed — "Book/Past Due", "Article/It Feels Easy…".
  // The leaf is the title; the prefix is what kind of thing it came out of, and
  // is worth a footer segment. The e-ink template put the source *domain* there
  // instead, which works when quotes come from all over the web but would print
  // "roamresearch.com" on every card in this building.
  const fullTitle = quote.metadata?.page_title || 'Roam Quote';
  const parts = fullTitle.split('/');
  const title = parts.length > 1 ? parts[parts.length - 1] : fullTitle;
  const kind = parts.length > 1 ? parts[0] : '';

  const railW = (type) => type.header * 0.62;
  const contentX = (type) => pad + railW(type) + type.header * 0.42;
  const titleTop = (type) => pad + type.header * 1.7;

  const boxFor = (type) => ({
    width: canvas.width - contentX(type) - pad,
    height: canvas.height - titleTop(type) - pad - type.footer * DATE_ROW,
  });

  const text = (contentText(quote) || 'No content available').trim().slice(0, TEXT_CAP);
  const { lines, type, box } = fitCard(context, text, ladder, boxFor, serifFont, MAX_QUOTE_SCALE);

  const x = contentX(type);
  const titleBaseline = pad + type.header;

  // Title, on the slot's baseline whether or not it had to shrink to fit the
  // width, so the dot, the title and the body stay on one grid.
  const heading = fitLine(context, `[[${title}]]`, sansFont,
    canvas.width - x - pad, type.header, type.header * HEAD_FLOOR, 700);
  context.fillStyle = palette.ink;
  context.textBaseline = 'alphabetic';
  context.fillText(heading.line, x, titleBaseline);

  // Rail: dot centred on the title's line box, rule beneath it, centred under
  // the dot and stopping where the byline starts.
  const railX = pad + railW(type) / 2;
  const dotR = railW(type) / 2;
  const dotY = titleBaseline - type.header * 0.32;
  context.fillStyle = palette.accent;
  context.beginPath();
  context.arc(railX, dotY, dotR, 0, 2 * Math.PI);
  context.fill();
  context.fillRect(
    railX - dotR * 0.3,
    dotY + dotR * 1.9,
    Math.max(1, dotR * 0.6),
    canvas.height - pad - type.footer * DATE_ROW - (dotY + dotR * 1.9),
  );

  // Centred while it still reads as a pull-quote, left-aligned once it does
  // not. Centring is what makes a two-line epigram look deliberate, and it is
  // also what makes a nine-line passage look like a wedding invitation: every
  // line starts in a different place, so the eye loses the return sweep.
  context.fillStyle = palette.ink;
  context.font = serifFont(type.body, type.weight);
  const align = lines.length <= CENTRE_MAX_LINES ? 'center' : 'left';
  drawLines(context, lines, x, titleTop(type), type, box.height, align, box.width);

  const segments = [quote.author || null, kind, ...dateSegments(quote)];
  drawSegments(context, segments, x, canvas.height - pad, canvas.width - x - pad, type, palette);
};

// Draw a content card sized to the plane it will be mapped onto.
// `plane` is { width, height } in model units; `text` is { body, header } in the
// same units. Omitting either falls back to a 4:3 canvas with proportional type.
// `pixelBudget` caps the canvas area — a museum with hundreds of live textures
// hangs smaller canvases than one with a fixed 164.
// `media` is an optional `{ image, isVideo }` still to hang under or beside the
// text; it arrives late (over the network) so callers redraw the same card once
// it lands.
export const drawContentCanvas = (
  content, plane = null, text = null, pixelBudget = undefined, media = null,
) => {
  const size = plane && plane.width > 0 && plane.height > 0
    ? { plane, canvas: canvasSizeFor(plane.width / plane.height, pixelBudget) }
    : { plane: { width: 4, height: 3 }, canvas: { width: 1024, height: 768 } };
  const metrics = text && text.body > 0
    ? text
    : { body: BODY_TEXT * size.plane.height, header: HEADER_TEXT * size.plane.height };

  const canvas = document.createElement('canvas');
  canvas.width = size.canvas.width;
  canvas.height = size.canvas.height;
  const context = canvas.getContext('2d');

  // Model units -> canvas pixels. Both axes share a scale because the canvas
  // carries the plane's aspect ratio (except at the clamped extremes).
  const scale = canvas.height / size.plane.height;
  const layout = {
    canvas,
    context,
    ladder: ladderFor(metrics.body * scale),
    // Padding off the short side keeps the optical margin square: 5% of a
    // 6:1 canvas's width is a hairline at the top and a chasm at the sides.
    pad: 0.05 * Math.min(canvas.width, canvas.height),
  };

  if (isRoamSource(content.source)) drawRoamQuote(layout, content);
  else drawTweet(layout, content, media);
  return canvas;
};
